import type { ReactNode } from 'react';

import { useHasAnyExperiments } from '~/features/experiments/hooks/use-experiments';

import { useOnboardingStatus } from '../hooks/use-onboarding-status';

import { CatalogImportRequiredPlaceholder } from './catalog-import-required-placeholder';
import { ConnectChannelPlaceholder } from './connect-channel-placeholder';

interface RequireExperimentsAccessProps {
  children: ReactNode;
}

/**
 * Page-level gate for the Experiments list. The real prerequisite for
 * experiments is a COMPLETED catalog import (onboarding finished), not merely a
 * connected channel — so when onboarding isn't done we name the actual blocker
 * instead of telling a user with a connected channel to "connect a channel"
 * (AGENT-195). Branches on the aggregate onboarding status:
 *   - `catalogImportStatus === 'COMPLETE'` → render the page.
 *   - not complete but a channel is already connected → catalog-import-not-
 *     complete placeholder (CTA resumes onboarding at Complete Import).
 *   - nothing connected at all → connect-a-channel placeholder.
 *
 * Loading + error render children: avoids flashing a placeholder during a cold
 * fetch and keeps the page reachable if the status endpoint is briefly down
 * (downstream queries surface the real failure).
 *
 * Existing experiments override the block entirely: a subsidiary that already
 * has experiments should keep seeing its list while a (re)import runs — the
 * page itself disables "New Experiment" with a tooltip in that state — rather
 * than have the whole view replaced by an onboarding placeholder. The
 * placeholder is therefore only shown when there is genuinely nothing to list.
 */
export function RequireExperimentsAccess({ children }: RequireExperimentsAccessProps) {
  const { data, isLoading, isError } = useOnboardingStatus();
  const importIncomplete =
    !isLoading && !isError && !!data && data.catalogImportStatus !== 'COMPLETE';
  // Only pay for the existence check when we might block (import incomplete).
  const { hasExperiments, isLoading: isExperimentsLoading } =
    useHasAnyExperiments(importIncomplete);

  // Block only when the tenant has nothing to show: import not complete AND no
  // existing experiments (once that check resolves — until then, render the list).
  if (data && importIncomplete && !isExperimentsLoading && !hasExperiments) {
    return data.connectedToChannel ? (
      <CatalogImportRequiredPlaceholder />
    ) : (
      <ConnectChannelPlaceholder />
    );
  }
  return <>{children}</>;
}
